/**
 * Project and Flow API Service
 * Handles all CRUD operations for projects and flows with Supabase backend
 */

import { ipoOf, hasIpo } from '@/utils/ipo';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache for session to avoid lock conflicts
let cachedSession = null;

/**
 * Initialize session listener to keep cache updated
 */
const initSessionCache = async () => {
  const { supabase } = await import('@/lib/supabase');
  
  // Get initial session
  const { data: { session } } = await supabase.auth.getSession();
  cachedSession = session;
  
  // Listen for changes
  supabase.auth.onAuthStateChange((event, session) => {
    cachedSession = session;
  });
};

// Initialize on module load
initSessionCache();

/**
 * Let the AuthProvider push the authoritative session into this cache. The
 * provider's session (getSession + onAuthStateChange) is reliable — it's what
 * the dashboard uses — whereas this module's own initSessionCache() can race a
 * navigator LockManager lock and leave the cache empty, which made the #481
 * paywall 402 a paid user's content. Keep both in sync.
 */
export const setAuthSession = (session) => {
  cachedSession = session;
};

/**
 * Get authentication token from cached session
 */
export const getAuthToken = () => {
  return cachedSession?.access_token || null;
};

/**
 * Robust token resolver — falls back to a fresh getSession() when the cache is
 * empty. Supabase's initial getSession() can return null under a navigator
 * LockManager lock and, for an already-logged-in user, onAuthStateChange never
 * fires to repopulate the cache. Without this the ADO #481 paywall 402s a paid
 * user's course content because no Authorization header is attached.
 */
const resolveAuthToken = async () => {
  if (cachedSession?.access_token) return cachedSession.access_token;
  try {
    const { supabase } = await import('@/lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      cachedSession = session;
      return session.access_token;
    }
  } catch { /* ignore — request proceeds unauthenticated */ }
  return null;
};

/**
 * Make authenticated API request
 */
const fetchWithAuth = async (url, options = {}) => {
  const token = await resolveAuthToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
};

/**
 * Like fetchWithAuth but returns the RAW Response (no throw, no auto-json) with
 * the Supabase bearer token attached. For callers that do their own
 * res.ok / res.json() handling — e.g. the Tests / MasterForever / CodeNext
 * panels reading /projects/{id}/test-cases, which is auth-gated as of ADO #463.
 */
export const authFetch = async (url, options = {}) => {
  const token = await resolveAuthToken();
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

/**
 * The test-cases URL for a project, told what is being graded.
 *
 * `practiceSlug` — ADO #511: the STORY being worked on. A capstone's stories
 * share one project, whose practice_item_slug names only the story that
 * created it; without this every story would show that story's tests. The
 * server honours it only for an item that builds in the same project.
 * `assessmentItemId` — ADO #500: the assessment item, which wins over both.
 */
export const projectTestCasesUrl = (projectId, { practiceSlug, assessmentItemId } = {}) => {
  const q = new URLSearchParams();
  if (practiceSlug) q.set('practice_slug', practiceSlug);
  if (assessmentItemId) q.set('assessment_item_id', assessmentItemId);
  const qs = q.toString();
  return `${process.env.REACT_APP_BACKEND_URL || ''}/api/projects/${encodeURIComponent(projectId)}/test-cases${qs ? `?${qs}` : ''}`;
};

// ============================================
// PROJECT OPERATIONS
// ============================================

/**
 * Get all projects for the current user
 */
export const getUserProjects = async (userId) => {
  return fetchWithAuth(`${API_URL}/api/projects?user_id=${userId}`);
};

/**
 * Create a new project
 */
export const createProject = async (userId, projectData) => {
  return fetchWithAuth(`${API_URL}/api/projects?user_id=${userId}`, {
    method: 'POST',
    body: JSON.stringify(projectData),
  });
};

/**
 * Get a project with its flows
 */
export const getProject = async (projectId) => {
  return fetchWithAuth(`${API_URL}/api/projects/${projectId}`);
};

/**
 * Update a project
 */
export const updateProject = async (projectId, updates) => {
  return fetchWithAuth(`${API_URL}/api/projects/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
};

/**
 * ADO #243 — Bulk replace project's global resources (class fields).
 * Sends the full list; backend wipes + re-inserts.
 */
export const replaceProjectGlobalResources = async (projectId, resources) => {
  return fetchWithAuth(`${API_URL}/api/projects/${projectId}/global-resources`, {
    method: 'PUT',
    body: JSON.stringify({ resources: resources || [] }),
  });
};

/**
 * Delete a project
 */
export const deleteProject = async (projectId) => {
  return fetchWithAuth(`${API_URL}/api/projects/${projectId}`, {
    method: 'DELETE',
  });
};

// ============================================
// FLOW OPERATIONS
// ============================================

/**
 * Get all flows for a project
 */
export const getProjectFlows = async (projectId) => {
  return fetchWithAuth(`${API_URL}/api/projects/${projectId}/flows`);
};

/**
 * Create a new flow in a project
 */
export const createFlow = async (projectId, flowData) => {
  return fetchWithAuth(`${API_URL}/api/projects/${projectId}/flows`, {
    method: 'POST',
    body: JSON.stringify(flowData),
  });
};

/**
 * Update a flow
 */
export const updateFlow = async (flowId, updates) => {
  return fetchWithAuth(`${API_URL}/api/flows/${flowId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
};

/**
 * Commit the DATA a run produced, without touching the flow's design.
 *
 * PostgreSQL keeps these apart: a committed statement makes data durable
 * immediately, and it never rewrites the server binary. So running a query
 * here must persist the files the engine wrote and must NOT persist the
 * student's half-finished edits to the flow.
 *
 * updateFlow cannot express that — flowStateToBackend carries `nodes`. The
 * backend merges only the seed maps into what is already stored, which also
 * makes it atomic against a design edit saved from another tab.
 */
export const persistFlowSeeds = async (flowId, seedFiles, seedFilesB64) => {
  return fetchWithAuth(`${API_URL}/api/flows/${flowId}/seeds`, {
    method: 'PATCH',
    body: JSON.stringify({
      seed_files: seedFiles || {},
      seed_files_b64: seedFilesB64 || {},
    }),
  });
};

// ---------------------------------------------------------------------------
// ADO #516 — include a library file from another project (read-only link).
// ---------------------------------------------------------------------------

/** The library files this learner may include (their own, and F2C's). */
export const listLibraries = async (projectId) =>
  fetchWithAuth(`${API_URL}/api/libraries${projectId ? `?project_id=${projectId}` : ''}`);

/** Mark one of your files as a library ('own'), or not (null). */
export const markLibrary = async (fileId, library) =>
  fetchWithAuth(`${API_URL}/api/files/${fileId}/library`, { method: 'PATCH', body: JSON.stringify({ library }) });

export const addInclude = async (projectId, fileId) =>
  fetchWithAuth(`${API_URL}/api/projects/${projectId}/includes`, { method: 'POST', body: JSON.stringify({ file_id: fileId }) });

export const removeInclude = async (projectId, fileId) =>
  fetchWithAuth(`${API_URL}/api/projects/${projectId}/includes/${fileId}`, { method: 'DELETE' });

/**
 * Delete a flow
 */
export const deleteFlow = async (flowId) => {
  return fetchWithAuth(`${API_URL}/api/flows/${flowId}`, {
    method: 'DELETE',
  });
};

// ============================================
// FILE OPERATIONS (ADO #215 — multi-file projects)
// ============================================

/**
 * Get all files for a project
 */
export const getProjectFiles = async (projectId) => {
  return fetchWithAuth(`${API_URL}/api/projects/${projectId}/files`);
};

/**
 * Create a new file in a project.
 * ADO #229 — callers may pass `file_class_type` ('class' | 'record') and
 * `record_components` (array of {name, type}) for Java record files.
 */
export const createFile = async (projectId, { name, display_order = 0, file_class_type, record_components }) => {
  const body = { name, display_order };
  if (file_class_type) body.file_class_type = file_class_type;
  if (record_components) body.record_components = record_components;
  return fetchWithAuth(`${API_URL}/api/projects/${projectId}/files`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * Rename or reorder a file.
 * ADO #229 — callers may also update `file_class_type` and `record_components`.
 */
export const renameFile = async (fileId, updates) => {
  // Back-compat: `updates` used to be a bare `name` string.
  const body = typeof updates === 'string' ? { name: updates } : (updates || {});
  return fetchWithAuth(`${API_URL}/api/files/${fileId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

/**
 * Delete a file (cascades to its flows)
 */
export const deleteFile = async (fileId) => {
  return fetchWithAuth(`${API_URL}/api/files/${fileId}`, {
    method: 'DELETE',
  });
};

// ============================================
// SESSION ENDPOINTS (ADO #237)
// ============================================

/**
 * List sessions for a file.
 */
export const listSessionsForFile = async (fileId) => {
  return fetchWithAuth(`${API_URL}/api/files/${fileId}/sessions`);
};

/**
 * Create a session inside a file.
 * sessionInput: { kind, name, access_specifier?, parent_session_id?, session_data?, display_order? }
 */
export const createSession = async (fileId, sessionInput) => {
  return fetchWithAuth(`${API_URL}/api/files/${fileId}/sessions`, {
    method: 'POST',
    body: JSON.stringify(sessionInput),
  });
};

/**
 * Update a session (partial).
 */
export const updateSession = async (sessionId, patch) => {
  return fetchWithAuth(`${API_URL}/api/sessions/${sessionId}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
};

/**
 * Delete a session (cascades to its flows).
 */
export const deleteSession = async (sessionId) => {
  return fetchWithAuth(`${API_URL}/api/sessions/${sessionId}`, {
    method: 'DELETE',
  });
};

/**
 * Convert backend session shape (snake_case) to frontend (camelCase).
 */
export const sessionFromBackend = (s) => ({
  id: s.id,
  fileId: s.file_id,
  parentSessionId: s.parent_session_id || null,
  kind: s.kind,
  name: s.name,
  accessSpecifier: s.access_specifier || 'public',
  sessionData: s.session_data || {},
  displayOrder: s.display_order || 0,
});

/**
 * Convert frontend session to backend create/update payload.
 */
export const sessionToBackend = (s) => ({
  kind: s.kind,
  name: s.name,
  access_specifier: s.accessSpecifier || 'public',
  parent_session_id: s.parentSessionId || null,
  session_data: s.sessionData || {},
  display_order: s.displayOrder ?? 0,
});

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Convert frontend flow state to backend format
 */
export const flowStateToBackend = (flow) => {
  const flowData = {
    nodes: flow.nodes,
    resources: flow.resources || [],
    parameters: flow.parameters || [],
    returnType: flow.returnType || 'void',
  };
  // Persist fileName for multi-file projects (ADO #215)
  if (flow.fileName) {
    flowData.fileName = flow.fileName;
  }
  // ADO #223 — persist flowType + accessSpecifier + legacy isStatic
  if (flow.flowType) flowData.flowType = flow.flowType;
  if (flow.accessSpecifier) flowData.accessSpecifier = flow.accessSpecifier;
  if (flow.isStatic !== undefined) flowData.isStatic = flow.isStatic;
  // ADO #400 — entry flow opted into command-line args (argc/argv).
  if (flow.usesCommandLineArgs) flowData.usesCommandLineArgs = true;
  // main's IPO (Input, Process, Output in words), when written.
  if (hasIpo(flow)) flowData.ipo = ipoOf(flow);
  // ADO #495 — a provided/locked flow (e.g. a contract "main" the student must
  // not edit; they implement the subflow it calls). Round-trips as freeform
  // JSONB so a save doesn't strip the lock off the provided flow.
  if (flow.locked) flowData.locked = true;
  // File I/O seed data (Option A) — sample contents for files the flow reads.
  // flow_data is freeform JSONB so this round-trips with no backend change.
  if (flow.seedFiles && Object.keys(flow.seedFiles).length > 0) {
    flowData.seedFiles = flow.seedFiles;
  }
  // Binary seeds, kept base64-encoded. A seed map holds JS strings, so raw
  // bytes -- a binary catalog, fixed-width tuples -- cannot ride in seedFiles
  // without being corrupted.
  if (flow.seedFilesB64 && Object.keys(flow.seedFilesB64).length > 0) {
    flowData.seedFilesB64 = flow.seedFilesB64;
  }
  // The step previews' shared sample values (src/preview/sampleStore.js), kept
  // like seedFiles. Codegen never reads them.
  if (flow.sampleValues && Object.keys(flow.sampleValues).length > 0) {
    flowData.sampleValues = flow.sampleValues;
  }
  const payload = {
    name: flow.name,
    is_entry: flow.isEntry || false,
    flow_data: flowData,
  };
  // ADO #237 — persist session binding when present (only for backend UUIDs)
  if (flow.sessionId && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(flow.sessionId)) {
    payload.session_id = flow.sessionId;
  }
  return payload;
};

/**
 * Convert backend flow to frontend format
 */
export const flowFromBackend = (backendFlow) => {
  const flowData = backendFlow.flow_data || {};
  const result = {
    id: backendFlow.id,
    name: backendFlow.name,
    isEntry: backendFlow.is_entry,
    // ADO #237 — bind to a session (one implementable unit)
    sessionId: backendFlow.session_id || null,
    nodes: flowData.nodes || [],
    resources: flowData.resources || [],
    parameters: flowData.parameters || [],
    returnType: flowData.returnType || 'void',
    // Initialize per-flow history
    history: [JSON.stringify({ nodes: flowData.nodes || [], resources: flowData.resources || [] })],
    historyIndex: 0,
    skipHistoryPush: false,
    // Initialize per-flow clipboard
    clipboard: null,
    clipboardAction: null,
    cutNodeId: null,
  };
  // Restore fileName for multi-file projects (ADO #215)
  if (flowData.fileName) {
    result.fileName = flowData.fileName;
  }
  // Restore File I/O seed data (Option A).
  if (flowData.seedFiles && typeof flowData.seedFiles === 'object') {
    result.seedFiles = flowData.seedFiles;
  }
  if (flowData.seedFilesB64 && typeof flowData.seedFilesB64 === 'object') {
    result.seedFilesB64 = flowData.seedFilesB64;
  }
  // The step previews' shared sample values (src/preview/sampleStore.js).
  if (flowData.sampleValues && typeof flowData.sampleValues === 'object') {
    result.sampleValues = flowData.sampleValues;
  }
  // ADO #223 — restore flowType + accessSpecifier + legacy isStatic
  // Migration fallback (in case the stored flow predates ADO #223):
  //   isEntry=true                → flowType='main'
  //   isEntry=false, isStatic=false → flowType='instance'
  //   else                         → flowType='static'
  if (flowData.flowType) {
    result.flowType = flowData.flowType;
  } else {
    result.flowType = backendFlow.is_entry
      ? 'main'
      : (flowData.isStatic === false ? 'instance' : 'static');
  }
  result.accessSpecifier = flowData.accessSpecifier || 'public';
  if (flowData.isStatic !== undefined) result.isStatic = flowData.isStatic;
  // ADO #400 — restore command-line-args opt-in.
  if (flowData.usesCommandLineArgs) result.usesCommandLineArgs = true;
  if (hasIpo(flowData)) result.ipo = ipoOf(flowData);
  // ADO #495 — restore the provided/locked flag so the canvas renders read-only.
  if (flowData.locked) result.locked = true;
  return result;
};

/**
 * Save entire project state (all flows)
 */
export const saveProjectState = async (projectId, projectName, flows, globalResources) => {
  const results = [];
  // ADO #516 — an included library is read here, never written: its flows and
  // its shared types belong to the library's own project. Saving them would
  // write through the link (the learner owns their toolbox, so the server
  // would accept it) or copy the library into this project.
  flows = (flows || []).filter((f) => !f.includedFrom);
  globalResources = (globalResources || []).filter((r) => !r.includedFrom);

  // ADO #243 — Persist globalResources (class fields) to the backend.
  // Best-effort: surface a result row but don't block flow saves on it.
  try {
    await replaceProjectGlobalResources(projectId, globalResources || []);
    results.push({ success: true, kind: 'globalResources', count: (globalResources || []).length });
  } catch (error) {
    results.push({ success: false, kind: 'globalResources', error: error.message });
  }

  for (const flow of flows) {
    try {
      // Check if flow has a backend ID (UUID format)
      const isExistingFlow = flow.id && flow.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
      
      if (isExistingFlow) {
        // Update existing flow
        const flowData = {
          nodes: flow.nodes,
          resources: flow.resources || [],
          parameters: flow.parameters || [],
          returnType: flow.returnType || 'void',
        };
        // Persist fileName for multi-file projects (ADO #215)
        if (flow.fileName) {
          flowData.fileName = flow.fileName;
        }
        // ADO #223 — persist flowType + accessSpecifier + legacy isStatic
        if (flow.flowType) flowData.flowType = flow.flowType;
        if (flow.accessSpecifier) flowData.accessSpecifier = flow.accessSpecifier;
        if (flow.isStatic !== undefined) flowData.isStatic = flow.isStatic;
        // ADO #400 — entry flow opted into command-line args (argc/argv).
        if (flow.usesCommandLineArgs) flowData.usesCommandLineArgs = true;
        if (hasIpo(flow)) flowData.ipo = ipoOf(flow);
        // File I/O seed data (Option A) — see flowStateToBackend.
        if (flow.seedFiles && Object.keys(flow.seedFiles).length > 0) {
          flowData.seedFiles = flow.seedFiles;
        }
        if (flow.seedFilesB64 && Object.keys(flow.seedFilesB64).length > 0) {
          flowData.seedFilesB64 = flow.seedFilesB64;
        }
        if (flow.sampleValues && Object.keys(flow.sampleValues).length > 0) {
          flowData.sampleValues = flow.sampleValues;
        }
        const updatePayload = {
          name: flow.name,
          // ADO #580 — the entry flag is part of the flow. Left off the update,
          // a flow made the entry after its first save (the load heal, or a
          // function renamed to main) stayed is_entry=false in the database.
          is_entry: !!flow.isEntry,
          flow_data: flowData,
        };
        // ADO #237 — persist session binding (only when it's already a backend UUID)
        if (flow.sessionId && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(flow.sessionId)) {
          updatePayload.session_id = flow.sessionId;
        }
        const result = await updateFlow(flow.id, updatePayload);
        results.push({ success: true, flow: result });
      } else {
        // Create new flow
        const result = await createFlow(projectId, flowStateToBackend(flow));
        results.push({ success: true, flow: result, oldId: flow.id, newId: result.id });
      }
    } catch (error) {
      results.push({ success: false, error: error.message, flowName: flow.name });
    }
  }
  
  return results;
};

/**
 * Load project state (returns flows in frontend format)
 */
export const loadProjectState = async (projectId) => (
  projectStateFromBackend(await getProject(projectId))
);

/**
 * A GET /api/projects/{id}-shaped payload in frontend format. Split from
 * loadProjectState for ADO #513: a capstone share link receives the same shape
 * from a public endpoint and must not fetch it again with the owner's auth.
 */
export const projectStateFromBackend = (project) => {
  // Practice projects legitimately start empty (P2 principle — student
  // builds everything). useFlowStore.loadProject handles flows=[] and
  // synthesizes Contract Flows from sessions, so no need to bail here.
  const flows = (project.flows || []).map(flowFromBackend);

  // Restore multi-file metadata (ADO #215)
  let files = [];
  if (project.files) {
    try {
      files = typeof project.files === 'string' ? JSON.parse(project.files) : project.files;
    } catch { /* ignore parse errors */ }
  }

  // ADO #215: Derive fileName from file_id FK when not stored in flow_data
  if (files.length > 0) {
    flows.forEach(flow => {
      if (flow.fileName) return; // Already has fileName from flow_data
      // Find the matching backend flow to get file_id
      const backendFlow = project.flows.find(bf => bf.id === flow.id);
      if (backendFlow && backendFlow.file_id) {
        const matchingFile = files.find(f => f.id === backendFlow.file_id);
        if (matchingFile) {
          flow.fileName = matchingFile.name;
        }
      }
    });
  }

  // ADO #237 — load sessions returned by GET /api/projects/{id}.
  // Backend includes them when files exist; map to camelCase frontend shape.
  const sessions = (project.sessions || []).map(sessionFromBackend);

  // ADO #516 — library files this project includes: merged in beside the
  // project's own, each marked with where it comes from, read-only. The C
  // build groups flows by fileName, so an included file is one more file.
  const globalResources = [...(project.global_resources || [])];
  const includes = [];
  for (const inc of project.included || []) {
    const from = inc.includedFrom;
    includes.push({ fileId: from.fileId, name: inc.file.name, projectId: from.projectId,
      projectName: from.projectName, missing: !!inc.missing });
    if (inc.missing) continue;
    files.push({ ...inc.file, includedFrom: from, readOnly: true });
    (inc.sessions || []).forEach((sess) => sessions.push({ ...sessionFromBackend(sess), includedFrom: from, readOnly: true }));
    (inc.flows || []).forEach((bf) => {
      const flow = flowFromBackend(bf);
      if (!flow.fileName) flow.fileName = inc.file.name;
      flows.push({ ...flow, includedFrom: from, readOnly: true, locked: true });
    });
    (inc.global_resources || []).forEach((r) => globalResources.push({ ...r, includedFrom: from, readOnly: true }));
  }

  return {
    projectId: project.id,
    projectName: project.name,
    track_type: project.track_type || 'programming',  // Include track type
    // ADO #507 — which client panel the builder fronts this project with.
    client_panel: project.client_panel || null,
    // ADO #583 — a project shared by several items keeps its Contract in view.
    practiceItemSlug: project.practice_item_slug || null,
    projectKey: project.project_key || null,
    language: project.language || 'c',  // Include project language
    flows,
    files,
    sessions,  // ADO #237
    // ADO #243 — backend now returns persisted class fields / project-shared resources
    globalResources,
    includes,  // ADO #516 — what this project includes, missing ones too
  };
};

export default {
  getUserProjects,
  createProject,
  getProject,
  updateProject,
  deleteProject,
  getProjectFlows,
  createFlow,
  updateFlow,
  deleteFlow,
  getProjectFiles,
  createFile,
  renameFile,
  deleteFile,
  saveProjectState,
  loadProjectState,
  flowStateToBackend,
  flowFromBackend,
  // ADO #237 — sessions
  listSessionsForFile,
  createSession,
  updateSession,
  deleteSession,
  sessionFromBackend,
  sessionToBackend,
  // ADO #243 — global resources persistence
  replaceProjectGlobalResources,
};
